/**
 * Customers → Travellers (REQ-018 AC-001, AC-002).
 *
 * Identity and commercial state. No clinical data reaches this page, because
 * the endpoint behind it does not return any — the wall is server-side, and
 * this component could not draw a medication list if it tried.
 *
 * The lifecycle stage is computed on the server on every read, so it cannot be
 * stale and cannot be corrected by hand. "Payment failed" is the one that wants
 * acting on, which is why it leads the filters and is coloured.
 */
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { AlertTriangle, Loader2, Search, Users, X } from "lucide-react";
import { useState } from "react";
import { useLocaleFormat } from "@/hooks/useLocaleFormat";
import apiClient from "@/services/api/apiClient";

type Stage =
  | "registered"
  | "trialing"
  | "active"
  | "past_due"
  | "cancelled"
  | "churned";

interface Customer {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  country: string | null;
  registeredAt: string | null;
  lastActiveAt: string | null;
  tier: string;
  subscriptionStatus: string | null;
  subscriptionType: string | null;
  currentPeriodEnd: string | null;
  stage: Stage;
}

interface CustomerDetail extends Customer {
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  singleTripExpiresAt: string | null;
  dunningGraceUntil: string | null;
  usage: { tripsPlanned: number; hasEmergencyCard: boolean };
}

const STAGES: { value: Stage | ""; label: string }[] = [
  { value: "", label: "Everyone" },
  { value: "past_due", label: "Payment failed" },
  { value: "active", label: "Active" },
  { value: "trialing", label: "Trialing" },
  { value: "registered", label: "Registered" },
  { value: "cancelled", label: "Cancelled" },
  { value: "churned", label: "Churned" },
];

const STAGE_STYLE: Record<Stage, string> = {
  active: "bg-emerald-100 text-emerald-800",
  trialing: "bg-sky-100 text-sky-800",
  registered: "bg-gray-100 text-gray-700",
  past_due: "bg-red-100 text-red-800",
  cancelled: "bg-amber-100 text-amber-800",
  churned: "bg-gray-100 text-gray-500",
};

const STAGE_LABEL: Record<Stage, string> = {
  active: "Active",
  trialing: "Trialing",
  registered: "Registered",
  past_due: "Payment failed",
  cancelled: "Cancelled",
  churned: "Churned",
};

export default function TravellersPage() {
  const fmt = useLocaleFormat();
  const dateOf = (iso: string | null) => (iso ? fmt.date(iso) : "—");
  const [search, setSearch] = useState("");
  const [stage, setStage] = useState<Stage | "">("");
  const [openId, setOpenId] = useState<string | null>(null);

  const customers = useQuery({
    queryKey: ["crm", "customers", search, stage],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search.trim().length > 1) params.set("q", search.trim());
      if (stage) params.set("stage", stage);
      const response = await apiClient.get<{
        data: { customers: Customer[] };
        meta: { total: number };
      }>(`/staff/customers?${params.toString()}`);
      return response.data;
    },
  });

  const detail = useQuery({
    queryKey: ["crm", "customer", openId],
    enabled: Boolean(openId),
    queryFn: async () => {
      const response = await apiClient.get<{
        data: { customer: CustomerDetail };
      }>(`/staff/customers/${openId}`);
      return response.data.data.customer;
    },
  });

  return (
    <div className="p-6">
      <header className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <h1 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
          <Users className="h-5 w-5 text-sky-600" /> Travellers
          {customers.data && (
            <span className="text-sm font-normal text-gray-500">
              {customers.data.meta.total}
            </span>
          )}
        </h1>

        <div className="flex flex-wrap items-center gap-2">
          <label className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Name or email"
              className="w-64 rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-sky-500 focus:outline-none"
            />
          </label>
          <select
            value={stage}
            onChange={(event) => setStage(event.target.value as Stage | "")}
            className="rounded-lg border border-gray-300 py-2 pl-3 pr-8 text-sm focus:border-sky-500 focus:outline-none"
          >
            {STAGES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </header>

      {customers.isError && (
        <div className="flex gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span>
            Customer records could not be read. If the audit trail is
            unavailable they are refused rather than served unlogged.
          </span>
        </div>
      )}

      {customers.isLoading && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}

      {customers.data && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-gray-200 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Tier</th>
                <th className="px-4 py-3 font-medium">Stage</th>
                <th className="px-4 py-3 font-medium">Registered</th>
                <th className="px-4 py-3 font-medium">Last active</th>
              </tr>
            </thead>
            <tbody>
              {customers.data.data.customers.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-8 text-center text-gray-500"
                  >
                    Nobody matches that.
                  </td>
                </tr>
              )}
              {customers.data.data.customers.map((customer) => (
                <tr
                  key={customer.id}
                  onClick={() => setOpenId(customer.id)}
                  className="cursor-pointer border-b border-gray-100 last:border-0 hover:bg-sky-50"
                >
                  <td className="px-4 py-3 font-medium text-gray-900">
                    {customer.firstName} {customer.lastName}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{customer.email}</td>
                  <td className="px-4 py-3 text-gray-600">{customer.tier}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${STAGE_STYLE[customer.stage]}`}
                    >
                      {STAGE_LABEL[customer.stage]}
                    </span>
                  </td>
                  <td className="px-4 py-3 tabular-nums text-gray-600">
                    {dateOf(customer.registeredAt)}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-gray-600">
                    {dateOf(customer.lastActiveAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openId && (
        <aside className="fixed inset-y-0 right-0 z-50 w-full max-w-md overflow-y-auto border-l border-gray-200 bg-white p-6 shadow-xl">
          <div className="mb-4 flex items-start justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              {detail.data
                ? `${detail.data.firstName} ${detail.data.lastName}`
                : "Customer"}
            </h2>
            <button
              onClick={() => setOpenId(null)}
              aria-label="Close"
              className="rounded p-1 text-gray-400 hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {detail.isLoading && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Opening…
            </div>
          )}

          {detail.data && (
            <dl className="space-y-3 text-sm">
              {(
                [
                  ["Email", detail.data.email],
                  ["Country", detail.data.country ?? "—"],
                  ["Tier", detail.data.tier],
                  ["Stage", STAGE_LABEL[detail.data.stage]],
                  ["Subscription", detail.data.subscriptionStatus ?? "none"],
                  ["Plan type", detail.data.subscriptionType ?? "—"],
                  ["Period ends", dateOf(detail.data.currentPeriodEnd)],
                  ["Single trip ends", dateOf(detail.data.singleTripExpiresAt)],
                  ["Dunning grace", dateOf(detail.data.dunningGraceUntil)],
                  ["Registered", dateOf(detail.data.registeredAt)],
                  ["Last active", dateOf(detail.data.lastActiveAt)],
                  ["Stripe customer", detail.data.stripeCustomerId ?? "—"],
                  ["Trips planned", String(detail.data.usage.tripsPlanned)],
                  [
                    "Emergency card",
                    detail.data.usage.hasEmergencyCard ? "Yes" : "No",
                  ],
                ] as [string, string][]
              ).map(([term, value]) => (
                <div key={term} className="flex gap-3">
                  <dt className="w-36 flex-shrink-0 text-gray-500">{term}</dt>
                  <dd className="break-all text-gray-900">{value}</dd>
                </div>
              ))}
            </dl>
          )}

          {/* Said plainly rather than left as an absence, so nobody spends time
              looking for a health tab that is never going to be here. */}
          <p className="mt-6 border-t border-gray-200 pt-4 text-xs leading-relaxed text-gray-500">
            Health records, documents and emergency card contents are not
            available in the back office, at any tier, for any role. Where a
            support case genuinely needs them, the traveller grants time-boxed
            access from their own account.
          </p>
        </aside>
      )}
    </div>
  );
}
